enum AllowedPreviewFileTypes {
    'application/pdf'
  }
  
  /**
   * @description
   * Creates and "clicks" a link for any set of blob data.
   * Downloads the data to the user file system.
   *
   * @param blob Blob data used.
   * @param fileName File name of downloaded file.
   */
  export const downloadBlob = (blob: Blob, fileName: string): void => {
    // Create blob URL and hidden anchor element.
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
  
    // Make anchor link a download link and click it.
    a.href = objectUrl;
    a.download = fileName;
    a.click();
  
    URL.revokeObjectURL(objectUrl);
    a.remove();
  };
  
  /** Strips any path component, so a header value can never redirect where the file lands. */
  const baseName = (value: string): string =>
    value.replace(/^.*[\\/]/, '').trim();

  /**
   * @description
   * Reads the intended download file name out of a response's `Content-Disposition` header.
   *
   * Server-side is the only place that knows the right name — a case number, a generated
   * timestamp — so hardcoding it in the caller (as the agency-manager list exports do) means the
   * name silently stops matching the content. This reads the header and falls back only when it is
   * absent or unparseable.
   *
   * Handles both header forms: the plain `filename="..."` and the RFC 5987 percent-encoded
   * `filename*=UTF-8''...`, which takes precedence when both are present. Any directory component
   * is stripped — a download name is a leaf name, never a path.
   *
   * @param response Response carrying the headers (an `HttpResponse` from `observe: "response"`).
   * @param fallback Name to use when the header cannot supply one.
   */
  export const filenameFromContentDisposition = (
    response: { headers?: { get(name: string): string | null } } | null | undefined,
    fallback: string
  ): string => {
    const header = response?.headers?.get('Content-Disposition');
    if (!header) return fallback;

    // RFC 5987 form wins: it carries the encoding, so it survives non-ASCII names intact.
    const extended = /filename\*\s*=\s*[^']*'[^']*'([^;]+)/i.exec(header);
    if (extended?.[1]) {
      try {
        const decoded = baseName(decodeURIComponent(extended[1].trim()));
        if (decoded) return decoded;
      } catch {
        // Malformed percent-encoding — fall through to the plain form rather than throwing.
      }
    }

    // `filename\s*=` cannot match `filename*=`, so the extended form never leaks in here.
    const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
    const name = baseName((plain?.[1] ?? '').trim());

    return name || fallback;
  };

  /**
   * @description
   * Opens blob data in the browser as a specific file type.
   *
   * @param blob Blob data used.
   * @param type Type of file to open.
   */
  export const previewBlob = async (blob: Blob, type: string): Promise<void> => {
    if (!(type in AllowedPreviewFileTypes)) throw Error(`File type ${type} is not a support type for preview.`);
  
    // Create blob URL and open new tab.
    const objectUrl = URL.createObjectURL(new Blob([blob], { type }));
    window.open(
      URL.createObjectURL(
        new Blob([blob], { type })
      )
    );
  
    URL.revokeObjectURL(objectUrl);
  };
  
  /**
   * @description
   * Converts a array to a CSV string.
   *
   * @param objArray Array to convert.
   * @param dataKeys Array of property keys to export from each item.
   * @param headerTitles Array of strings used for the headers.
   *
   * @returns Formatted CSV string.
   */
  export const convertToCsv = (objArray: any[], dataKeys: string[], headerTitles: string[]): string => {
    const array = typeof objArray != 'object' ? JSON.parse(objArray) : objArray;
    let str = '';
    let row = '';
    for (const index in headerTitles) {
      row += headerTitles[index] + ',';
    }
  
    row = row.slice(0, -1);
    str += row + '\r\n';
  
    for (let i = 0; i < array.length; i++) {
      let line = '';
      for (const index in dataKeys) {
        const head = dataKeys[index];
        line += array[i][head] + ',';
      }
  
      str += line + '\r\n';
    }
  
    return str;
  };
  
  /**
   * @description
   * Converts and downloads an array of data to a CSV file.
   *
   * @param data Array of data.
   * @param dataKeys Array of property keys to export from each item.
   * @param headerTitles Array of strings used for the headers.
   * @param fileName Name of exported file.
   */
  export const downloadAsCsv = (
    data: any[],
    dataKeys: string[],
    headerTitles: string[],
    fileName = 'Exported Data'
  ): void => {
    const csvData = convertToCsv(data, dataKeys, headerTitles);
    const blob = new Blob(['\ufeff' + csvData], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const isSafariBrowser =
      navigator.userAgent.indexOf('Safari') != -1 &&
      navigator.userAgent.indexOf('Chrome') == -1;
  
    // If Safari open in new window to save file with random filename.
    if (isSafariBrowser) {
      link.setAttribute('target', '_blank');
    }
  
    link.setAttribute('href', url);
    link.setAttribute('download', `${fileName}.csv`);
    link.click();
  
    link.remove();
    URL.revokeObjectURL(url);
  };
  